import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { getApiErrorMessage } from '@/shared/api/axios';
import { usePayrollSetup, useSaveComponent, useSaveStructure, type CalcType, type PayrollAccess, type SalaryComponent, type SalaryStructure } from './api';

const CALC_LABEL: Record<CalcType, string> = {
  percent_of_base: '% of the monthly salary',
  fixed_amount: 'Fixed amount',
  percent_of_component: '% of another component',
  slab: 'Slabs by salary',
};

function describe(c: SalaryComponent) {
  switch (c.calcType) {
    case 'percent_of_base': return `${c.defaultValue}% of salary`;
    case 'fixed_amount': return `₹${Number(c.defaultValue ?? 0).toLocaleString('en-IN')}`;
    case 'percent_of_component': return `${c.defaultValue}% of ${c.calcRef}`;
    case 'slab': return c.slabs.map((s) => `from ₹${s.minBase.toLocaleString('en-IN')}: ${s.value}%`).join(' · ');
  }
}

const blank = (order: number): SalaryComponent => ({
  id: '', code: '', name: '', type: 'earning', calcType: 'fixed_amount', calcRef: null, calcOrder: order, defaultValue: 0,
  slabs: [{ minBase: 0, value: 0 }], taxable: true, proRata: false, statutory: false, regimeAwareTax: false, active: true,
});

function ComponentModal({ component, isNew, all, onClose }: { component: SalaryComponent | null; isNew: boolean; all: SalaryComponent[]; onClose: () => void }) {
  const save = useSaveComponent();
  const [draft, setDraft] = useState<SalaryComponent | null>(null);
  if (component && draft?.code !== component.code) setDraft(component);
  if (!component && draft) setDraft(null);
  if (!draft) return null;
  const set = <K extends keyof SalaryComponent>(key: K, value: SalaryComponent[K]) => setDraft({ ...draft, [key]: value });

  const submit = async () => {
    try {
      const { id: _id, ...body } = draft;
      void _id;
      await save.mutateAsync({ code: draft.code, isNew, body: { ...body, defaultValue: draft.calcType === 'slab' ? null : Number(draft.defaultValue) } });
      toast.success('Component saved.');
      onClose();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? 'New salary component' : `Edit ${draft.code}`} size="lg" footer={
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={save.isPending}>Save</Button></div>
    }>
      <div className="grid max-h-[65vh] grid-cols-1 gap-3 overflow-auto py-2 md:grid-cols-2">
        <Input label="Code" value={draft.code} disabled={!isNew} onChange={(e) => set('code', e.target.value.toUpperCase())} hint="Letters, digits and underscores, e.g. TRAVEL" />
        <Input label="Name on the payslip" value={draft.name} onChange={(e) => set('name', e.target.value)} />
        <Select label="Type" value={draft.type} options={[{ value: 'earning', label: 'Earning' }, { value: 'deduction', label: 'Deduction' }]} onChange={(e) => set('type', e.target.value)} />
        <Input label="Order of calculation" type="number" min={1} value={draft.calcOrder} onChange={(e) => set('calcOrder', Number(e.target.value))} hint="Lower numbers are worked out first." />
        <Select label="How it is worked out" value={draft.calcType} options={(Object.keys(CALC_LABEL) as CalcType[]).map((k) => ({ value: k, label: CALC_LABEL[k] }))} onChange={(e) => set('calcType', e.target.value)} />
        {draft.calcType !== 'slab' && (
          <Input label={draft.calcType === 'fixed_amount' ? 'Amount (₹)' : 'Percentage'} type="number" min={0} value={draft.defaultValue ?? ''} onChange={(e) => set('defaultValue', e.target.value === '' ? null : Number(e.target.value))} />
        )}
        {draft.calcType === 'percent_of_component' && (
          <Select label="Percentage of" value={draft.calcRef ?? ''} placeholder="Choose a component" options={all.filter((c) => c.code !== draft.code).map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))} onChange={(e) => set('calcRef', e.target.value)} />
        )}
        {draft.calcType === 'slab' && (
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between"><span className="text-sm font-medium">Slabs (percentage of salary)</span><Button size="sm" variant="ghost" onClick={() => set('slabs', [...draft.slabs, { minBase: 0, value: 0 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Add slab</Button></div>
            {draft.slabs.map((s, i) => (
              <div key={i} className="flex gap-2">
                <Input type="number" min={0} placeholder="Salary from (₹)" value={s.minBase} onChange={(e) => set('slabs', draft.slabs.map((x, j) => (j === i ? { ...x, minBase: Number(e.target.value) } : x)))} />
                <Input type="number" min={0} max={100} placeholder="%" value={s.value} onChange={(e) => set('slabs', draft.slabs.map((x, j) => (j === i ? { ...x, value: Number(e.target.value) } : x)))} />
                <Button size="sm" variant="ghost" onClick={() => set('slabs', draft.slabs.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-4 text-sm md:col-span-2">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={draft.proRata} onChange={(e) => set('proRata', e.target.checked)} /> Reduced for unpaid days</label>
          <label className="flex items-center gap-1.5" title="Only applied while statutory deductions are switched on in Settings"><input type="checkbox" checked={draft.statutory} onChange={(e) => set('statutory', e.target.checked)} /> Statutory (PF, ESI, tax...)</label>
          {draft.type === 'deduction' && <label className="flex items-center gap-1.5" title="Replaced by the income-tax calculation when statutory deductions are on"><input type="checkbox" checked={draft.regimeAwareTax} onChange={(e) => set('regimeAwareTax', e.target.checked)} /> Is the income tax (TDS)</label>}
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={draft.active} onChange={(e) => set('active', e.target.checked)} /> Active</label>
        </div>
      </div>
    </Modal>
  );
}

function StructureModal({ structure, isNew, components, onClose }: { structure: SalaryStructure | null; isNew: boolean; components: SalaryComponent[]; onClose: () => void }) {
  const save = useSaveStructure();
  const [draft, setDraft] = useState<SalaryStructure | null>(null);
  if (structure && draft?.code !== structure.code) setDraft(structure);
  if (!structure && draft) setDraft(null);
  if (!draft) return null;
  const toggle = (code: string) => setDraft({ ...draft, componentCodes: draft.componentCodes.includes(code) ? draft.componentCodes.filter((c) => c !== code) : [...draft.componentCodes, code] });
  const submit = async () => {
    try {
      const { id: _id, ...body } = draft;
      void _id;
      await save.mutateAsync({ code: draft.code, isNew, body });
      toast.success('Structure saved.');
      onClose();
    } catch (err) { toast.error(getApiErrorMessage(err)); }
  };
  return (
    <Modal open onClose={onClose} title={isNew ? 'New salary structure' : `Edit ${draft.name}`} size="md" footer={
      <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={save.isPending}>Save</Button></div>
    }>
      <div className="space-y-3 py-2">
        <Input label="Code" value={draft.code} disabled={!isNew} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} />
        <Input label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
        <Input label="Description (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        <div>
          <p className="mb-1 text-sm font-medium">Components in this structure</p>
          <div className="max-h-48 space-y-1 overflow-auto rounded border border-outline p-2">
            {components.map((c) => (
              <label key={c.code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.componentCodes.includes(c.code)} onChange={() => toggle(c.code)} />
                {c.name} <span className="text-xs text-on-surface-variant">({c.type}{c.statutory ? ', statutory' : ''})</span>
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active</label>
      </div>
    </Modal>
  );
}

export function SalarySetupTab({ access }: { access: PayrollAccess }) {
  const { data: setup, isLoading } = usePayrollSetup();
  const [component, setComponent] = useState<{ value: SalaryComponent; isNew: boolean } | null>(null);
  const [structure, setStructure] = useState<{ value: SalaryStructure; isNew: boolean } | null>(null);
  if (isLoading || !setup) return <p className="p-8 text-center text-on-surface-variant">Loading…</p>;
  const statutoryOn = setup.settings.applyStatutoryDeductions;

  return (
    <div className="space-y-8 p-6">
      {!access.canFinalize && <p className="rounded-md bg-surface-variant p-3 text-sm text-on-surface-variant">You can look at how pay is built here. Changing it needs payroll approval rights.</p>}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div><h3 className="font-semibold">Salary components</h3><p className="text-xs text-on-surface-variant">The lines of a payslip. Statutory deductions are {statutoryOn ? <strong>on</strong> : <strong>off</strong>} (see Settings).</p></div>
          {access.canFinalize && <Button size="sm" onClick={() => setComponent({ value: blank(Math.max(0, ...setup.components.map((c) => c.calcOrder)) + 1), isNew: true })}><Plus className="mr-1 h-4 w-4" /> Add component</Button>}
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-variant text-xs uppercase text-on-surface-variant"><tr><th className="px-3 py-2">Order</th><th className="px-3 py-2">Component</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Worked out as</th><th className="px-3 py-2">Flags</th><th /></tr></thead>
          <tbody className="divide-y divide-outline/50">
            {setup.components.map((c) => (
              <tr key={c.code} className={c.active ? '' : 'opacity-50'}>
                <td className="px-3 py-2">{c.calcOrder}</td>
                <td className="px-3 py-2"><div className="font-medium">{c.name}</div><div className="text-xs text-on-surface-variant">{c.code}</div></td>
                <td className="px-3 py-2 capitalize">{c.type}</td>
                <td className="px-3 py-2 text-xs">{describe(c)}</td>
                <td className="space-x-1 px-3 py-2">
                  {c.proRata && <Badge tone="info">pro-rata</Badge>}
                  {c.statutory && <Badge tone={statutoryOn ? 'warning' : 'neutral'}>{statutoryOn ? 'statutory' : 'statutory (off)'}</Badge>}
                  {!c.active && <Badge tone="neutral">inactive</Badge>}
                </td>
                <td className="px-3 py-2 text-right">{access.canFinalize && <Button size="sm" variant="outline" onClick={() => setComponent({ value: c, isNew: false })}>Edit</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div><h3 className="font-semibold">Salary structures</h3><p className="text-xs text-on-surface-variant">Which components apply to a group of employees. Assign one to each employee under Employees.</p></div>
          {access.canFinalize && <Button size="sm" onClick={() => setStructure({ value: { id: '', code: '', name: '', description: '', componentCodes: [], active: true }, isNew: true })}><Plus className="mr-1 h-4 w-4" /> Add structure</Button>}
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-surface-variant text-xs uppercase text-on-surface-variant"><tr><th className="px-3 py-2">Structure</th><th className="px-3 py-2">Components</th><th /></tr></thead>
          <tbody className="divide-y divide-outline/50">
            {setup.structures.map((s) => (
              <tr key={s.code} className={s.active ? '' : 'opacity-50'}>
                <td className="px-3 py-2"><div className="font-medium">{s.name}</div><div className="text-xs text-on-surface-variant">{s.code}{s.description ? ` · ${s.description}` : ''}</div></td>
                <td className="px-3 py-2 text-xs">{s.componentCodes.join(', ')}</td>
                <td className="px-3 py-2 text-right">{access.canFinalize && <Button size="sm" variant="outline" onClick={() => setStructure({ value: s, isNew: false })}>Edit</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <ComponentModal component={component?.value ?? null} isNew={component?.isNew ?? false} all={setup.components} onClose={() => setComponent(null)} />
      <StructureModal structure={structure?.value ?? null} isNew={structure?.isNew ?? false} components={setup.components} onClose={() => setStructure(null)} />
    </div>
  );
}
